import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Camera, Loader2, Bike, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function RiderCheckin() {
  useDocumentTitle("Daily check-in");
  const { qrToken } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/delivery/public/vehicle/${qrToken}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setError("This QR is not valid. Please contact the operator."));
  }, [qrToken]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f); fd.append("category", "daily");
    try {
      const { data } = await axios.post(`${API}/delivery/public/upload/${qrToken}`, fd);
      setPhotoId(data.id);
    } catch { /* allow check-in without photo */ } finally { setUploading(false); }
  };

  const checkin = async () => {
    if (!phone) return;
    setBusy(true);
    try {
      let lat, long;
      await new Promise((res) => navigator.geolocation?.getCurrentPosition(
        (p) => { lat = p.coords.latitude; long = p.coords.longitude; res(); }, () => res(), { timeout: 5000 }));
      const { data } = await axios.post(`${API}/delivery/public/checkin`, { qr_token: qrToken, phone, photo_file: photoId, lat, long });
      setDone(data);
    } catch (e) { setError(e.response?.data?.detail || "Check-in failed"); } finally { setBusy(false); }
  };

  if (error && !info) return <Centered><p className="text-status-recovery">{error}</p></Centered>;
  if (!info) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;

  return (
    <Centered>
      <div data-testid="rider-checkin-page" className="w-full max-w-sm rounded-[24px] border border-tenant-border bg-tenant-surface p-6 shadow-tenant-float">
        <div className="flex items-center gap-2 text-tenant-primary"><Bike className="h-5 w-5" /><span className="font-bold">{info.logo_text || "Daily check-in"}</span></div>
        <p className="mt-1 text-sm text-muted-foreground">{info.vehicle.reg_number} · {info.vehicle.model}</p>

        {done ? (
          <div data-testid="checkin-success" className="mt-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-status-available" />
            <p className="mt-3 font-semibold">{done.already_checked_in ? "Already checked in today" : "Checked in for today ✓"}</p>
            <p className="mt-1 text-sm text-muted-foreground">Ride safe! Remember to check in again tomorrow.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <p className="text-sm">Scan in to mark you're working today.</p>
            <Input data-testid="checkin-phone" placeholder="Your registered phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <label data-testid="checkin-photo" className="flex cursor-pointer items-center justify-center gap-2 rounded-tenant-button border border-dashed border-tenant-border py-4 text-sm text-muted-foreground">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {photoId ? "Photo added ✓" : "Take a photo with the vehicle"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
            </label>
            {error && <p className="flex items-center gap-1 text-sm text-status-recovery"><AlertTriangle className="h-4 w-4" /> {error}</p>}
            <Button data-testid="submit-checkin" onClick={checkin} disabled={busy || !phone} className="w-full gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Check in</Button>
            <p className="text-center text-[11px] text-muted-foreground">Holding the vehicle without checking in is charged ₹100/idle day. Please surrender if not in use.</p>
          </div>
        )}
      </div>
    </Centered>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-tenant-bg p-4">{children}</div>;
}
